<template lang="pug">
.register-careimage-table
  .careimage-header.d-flex.align-items-stretch.flex-nowrap
    .name-column 部屋名・利用者名
    .file-selecter 
      HelpIcon(:title="'選択可能なファイルについて'" :text="'jpegもしくはpdfを選択可能です。pdfはjpegファイルに変換してご家族に送ります。'")
    .image-column(v-for="n in registerFileUploadLimit") {{ n }}枚目
  .resident-careimage.d-flex.align-items-stretch.flex-nowrap(v-if="0 < residents.length" v-for="resident in residents" :key="resident.id")
    ResidentCareimageRow(
      v-model="resident.files"
      :date="date"
      :id="resident.id"
      :name="resident.name"
      :room="resident.room"
      :limit="registerFileUploadLimit"
      @add="onAdd($event, resident)"
      @delete="onDelete($event, resident)"
      @change="onChange(resident)"
    )

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import ResidentsService, { ResidentGetModel } from "@/commons/services/Residents";
import CareimageModel from "@/commons/models/Careimage";
import CareimagesService from "@/commons/services/Careimages";
import S3UrlService from "@/commons/services/S3Url";

import { ResidentCareimageModel } from "@/pc/pages/careimages/CareimageMultipleRegister.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ResidentCareimageRow from "@/pc/components/ResidentCareimageRow.vue";

/**
 * 介護記録一括登録画面コンポーネント
 */
@Component({
  components: {
    HelpIcon,
    PageLoading,
    ResidentCareimageRow
  }
})
export default class ResidentsCareimageTable extends Vue {
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 介護記録サービス */
  @Inject("CareimagesService") careimagesService!: CareimagesService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** 表示対象の介護記録日付 */
  @Prop({ required: true, type: Number })
  date!: number;
  /** 家族が存在する利用者に限定するか */
  @Prop({ default: () => false, type: Boolean })
  existFamily!: boolean;
  /** 利用者 ＋ 1件の介護記録(親と値を同期する) */
  @Prop({ required: true, type: Array })
  value!: Array<ResidentCareimageModel>;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 介護記録画像登録数 */
  readonly registerFileUploadLimit: number = Setting.careimage.fileUploadLimit;
  /** 利用者 ＋ 1件の介護記録 */
  residents: Array<ResidentCareimageModel> = [];

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    this.residents = await this.createResidentCareimageModel();
    this.$emit("input", this.residents);

    this.isBusy = false;

    return;
  }

  /**
   * ### 画面Modelを作成する
   *
   * - 利用者を取得し、利用者のpropのdateにひもづく介護記録を加えて画面表示用Modelを作る
   * - 処理高速化のために利用者にひもづく介護記録は非同期で取得する
   * - 非同期で介護記録を取得するので、全情報取得後に利用者は並び替える
   *
   * @returns 画面Model
   */
  public async createResidentCareimageModel(): Promise<Array<ResidentCareimageModel>> {
    const models: Array<ResidentCareimageModel> = [];
    const residentGroupId: string = this.$route.params.residentgroup;

    const residents: Array<ResidentGetModel> = await this.getResidents(this.existFamily);
    const results: Array<any> = await Promise.all(
      residents.map(async (resident: ResidentGetModel) => {
        try {
          // 前画面から引き継いだ利用者グループIDによるフィルタリングを行う
          if (residentGroupId != "all") {
            if (Number(residentGroupId) != resident.groupId) {
              return;
            }
          }

          let model: any = Object.assign({}, resident);

          const careimages: Array<CareimageModel> = await this.getCareimage(resident.id, this.date);
          if (0 < careimages.length) {
            model.careimage = Object.assign({}, careimages[0]);

            // 画像URLはS3のURLなので画面表示できるように変換し、key情報をつけて保持する
            let files: Array<any> = [];
            for (let i = 0; i < careimages[0].imageUrls.length; i++) {
              const dataUrl: string = await this.getS3DataUrl(careimages[0].imageUrls[i]);
              files.push({
                dataUrl: dataUrl,
                key: i.toString(),
                name: this.getFileName(careimages[0].imageUrls[i])
              });
            }
            model.files = files;
            model.edited = false;
          } else {
            // 介護記録が未登録の場合は新規に作る
            const careimage: CareimageModel = {
              isPublic: false,
              datetime: this.date,
              deleteFlag: false,
              facilityId: userStore.loginUser.facilityId,
              imageUrls: [],
              residentId: resident.id,
              residentName: resident.name,
              residentRoom: resident.room,
              writtenBy: userStore.loginUser.name,
              writerId: userStore.loginUser.userId
            };
            model.careimage = careimage;
            model.files = [];
            model.edited = false;
          }

          models.push(model);
        } catch (error) {
          // Promise.all()で並列に実行されるので例外を投げなおすのではなく例外を戻り値として返す
          return error;
        }
      })
    );

    // 非同期処理中に例外が発生していればresultの中にSystemerrorが格納されているのでチェックする
    for (const result of results) {
      if (result) {
        // エラーハンドル処理を実行する
        errorHandle(result, this);
      }
    }

    const sortedModels = this.sortResidents(models);

    // ソートした後、表示対象の利用者を絞り込む
    const perPage: number = Number(this.$route.params.perpage);
    const page: number = Number(this.$route.params.page);
    let slicedModels: Array<any> = [];

    for (let i = 0; i * perPage < sortedModels.length; i++) {
      slicedModels.push(sortedModels.slice(i * perPage, (i + 1) * perPage));
    }

    return slicedModels[page - 1];
  }

  /**
   * ### 利用者を取得する
   */
  public async getResidents(existFamily: boolean): Promise<Array<ResidentGetModel>> {
    const startIndex: number = Number(this.$route.params.start);
    const endIndex: number = Number(this.$route.params.end);
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex
    };
    // APIの仕様は以下なので、全件取得の場合はパラメータ自体渡さない
    // true=家族がいる利用者のみ取得
    // false=家族がいない利用者のみ取得
    if (existFamily == true) {
      params["existFamily"] = true;
    }

    const response: Array<ResidentGetModel> = await this.residentsService.gets(userStore, params);

    return response;
  }

  /**
   * ### 介護記録取得
   *
   * @param residentId 介護記録に紐づく利用者ID
   * @param from 始点の日付
   * @param to 終点の日付
   */
  public async getCareimage(residentId: number, date: number): Promise<Array<CareimageModel>> {
    const queryParams: any = {
      start: date,
      end: date
    };

    const response: Array<CareimageModel> = await this.careimagesService.get(userStore, queryParams, residentId);

    return response;
  }

  /**
   * ### 閲覧可能なS3のURLを取得
   *
   * @param url 元URL
   */
  public async getS3DataUrl(dataUrl: string): Promise<string> {
    const response: string = await this.s3UrlService.getDataUrl(userStore, Setting.backets.imageExpireHour, dataUrl);

    return response;
  }

  /**
   * ### URLからファイル名を取得する
   */
  public getFileName(url: string): string {
    const _file: Array<string> = url.split("/");
    return _file[_file.length - 1];
  }

  /**
   * ### 利用者をソートする
   *
   * - 部屋名の昇順でソートする
   * - 部屋名は重複がありえるため、利用者IDでソート後に部屋名でソートする
   *
   * @param residents ソート対象の利用者
   */
  public sortResidents(residents: Array<ResidentCareimageModel>): Array<ResidentCareimageModel> {
    // 利用者IDでソート
    const idSortedResidents: Array<ResidentCareimageModel> = residents.concat().sort((a, b) => {
      if (a.id < b.id) {
        return -1;
      }
      if (a.id > b.id) {
        return 1;
      }
      return 0;
    });

    // 部屋名でソート
    const sortedResidents: Array<ResidentCareimageModel> = idSortedResidents.concat().sort((a, b) => {
      if (a.room < b.room) {
        return -1;
      }
      if (a.room > b.room) {
        return 1;
      }
      return 0;
    });

    return sortedResidents;
  }

  /**
   * ### ファイルを追加する
   *
   * - ファイルを並び替えたり削除しているとkey名がズレるので、ファイル追加のタイミングでkey名を更新する
   */
  public onAdd(files: Array<any>, resident: ResidentCareimageModel): void {
    resident.edited = true;
    resident.files = resident.files.concat(files);

    for (let i = 0; i < resident.files.length; i++) {
      resident.files[i].key = i.toString();
    }
    this.$emit("input", this.residents);

    return;
  }

  /**
   * ### 指定されたファイルを削除する
   */
  public onDelete(index: number, resident: ResidentCareimageModel): void {
    resident.edited = true;
    resident.files.splice(index, 1);
    this.$emit("input", this.residents);
    return;
  }

  /**
   * ### 並び替えが行われたので編集フラグをtrueにする
   */
  public onChange(resident: ResidentCareimageModel): void {
    resident.edited = true;
    this.$emit("input", this.residents);
    return;
  }
}
</script>
<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.register-careimage-table {
  height: calc(100vh - 280px);
  overflow-y: auto;
  overflow-x: auto;
  // 一番外枠に1pxのボーダーを引き、内部columnでボーダーを調整してデザインを満たす
  border: 1px solid $border-darkgray;
  margin-bottom: 10px;

  // header container、body container共通のスタイル
  .careimage-header,
  .resident-careimage {
    // 画面幅が小さい場合に、stickyによる回り込みで子要素が画面外に行くとstickyが動作しなくなる
    // そのため子要素の幅分、親要素の幅をとるように指定
    width: max-content;
  }

  // 枠線のデザイン対応
  .careimage-header {
    & > div {
      border-left: solid 2px $adminlte-box-bordercolor;
      border-bottom: solid 1px $border-darkgray;
    }
    & > div:first-child {
      border-left: 0;
    }
  }

  .careimage-header {
    // 回り込み時に下にある要素が見えてしまうので、背景色を指定
    & > div {
      background-color: $bg-gray;
    }

    // headerはスクロールしても固定したいのでheader containerはstickyを指定
    position: sticky;
    top: 0;
    z-index: 200;

    // 縦横列固定なので、header行かつ、resident-name列はz-indexを一番大きくする
    .name-column {
      position: sticky;
      left: 0;
      top: 0;
      z-index: 300;
      padding: 8px;
    }

    @include bold-font();
    text-align: center;
  }
  // 共通で使用する列のスタイル
  // 最低幅を指定しないと画面が小さい時に動的に幅が小さくなってしまう
  .name-column {
    min-width: 183px;
    // 利用者名列は固定したいのでstickyにする
    position: sticky;
    left: 0;
    z-index: 100;
  }
  .file-selecter {
    min-width: 320px;
    padding: 8px;
  }
  .image-column {
    min-width: 307px;
    max-width: 307px;
    padding: 8px;
  }
}
</style>
